import type { MouseEventHandler, PropsWithChildren } from 'react';
import { memo } from 'react';

import { Karma } from '@ui/uikit/components/icons/mono/Karma';
import { cn } from '@ui/uikit/utils';
import { Image } from '@utilities/Image';
import { Link } from '@utilities/Link';

import { KarmaIndicator } from '../../KarmaIndicator';
import styles from './UserAvatar.module.scss';

type AvatarSize = 'small' | 'medium';

const AVATAR_SIZES: Record<AvatarSize, number> = {
  small: 24,
  medium: 32,
} as const;

interface UserProps {
  name: string | null;
  icon: string;
  karma?: number;
  profileLink?: string;
  membership?: "FREE" | "PLUS" | "PREMIUM" | "PREMIUM_PLUS";
}

export interface UserAvatarProps {
  /**
   * The user to display in the avatar
   */
  user: UserProps;
  /**
   * Called when the avatar is clicked
   */
  onClick?: MouseEventHandler<HTMLElement>;
  /**
   * The size of the avatar
   * @default small
   */
  size?: AvatarSize;
  /**
   * A short text displayed to the right of the karma indicator
   */
  description?: string;
  /**
   * Whether to render the component vertically or horizontally
   */
  variant?: 'vertical' | 'horizontal';
}

export const UserAvatar = memo(
  ({
    user,
    onClick,
    size = 'small',
    description,
    variant = 'horizontal',
  }: PropsWithChildren<UserAvatarProps>) => {
    const props = {
      className: styles.self,
      onClick,
      'data-size': size,
      'data-variant': variant,
    };

    const isVertical = variant === 'vertical';

    const content = (
      <>
        <span
          className={cn({
            [styles.membershipBorder]: !!user.membership && user.membership !== 'FREE',
            [styles.membershipBorder__plus]: user.membership === 'PLUS',
            [styles.membershipBorder__premium]: user.membership === 'PREMIUM',
            [styles.membershipBorder__premiumPlus]: user.membership === 'PREMIUM_PLUS',
          })}
        >
          <Image
            src={user.icon}
            width={AVATAR_SIZES[size]}
            height={AVATAR_SIZES[size]}
            alt={user.name ? `${user.name}'s avatar` : 'User avatar'}
            className={styles.image}
            data-size={size}
          />
        </span>

        <div className={styles.name} data-variant={variant}>
          {user.name}
        </div>

        <div className={styles.meta}>
          {user.karma && (
            <KarmaIndicator amount={user.karma} labelSize={isVertical ? 'small' : 'medium'} />
          )}
          {description && (
            <span className={styles.description} data-variant={variant}>
              {!isVertical && <Karma className={styles.descriptionIcon} />}
              {description}
            </span>
          )}
        </div>
      </>
    );

    if (user.profileLink) {
      return (
        <Link href={user.profileLink} {...props}>
          {content}
        </Link>
      );
    }

    return <div {...props}>{content}</div>;
  },
);
